import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { buildingsApi } from '../../api/buildings';
import AddressAutocompleteField from '../AddressAutocompleteField';
import GatewayMapView from '../Gateways/GatewayMapView';

const normalizeCoordinate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const qcGeoHint = (qcGeo) => {
  const code = String(qcGeo ?? '').trim();
  if (code === '0') return null;
  if (code === '1') return 'Найдены координаты ближайшего дома — точка может быть смещена на десятки метров.';
  if (code === '2') return 'Координаты на уровне улицы — уточните точку кликом по карте.';
  if (code === '3' || code === '4') return 'Координаты слишком грубые. Уточните точку кликом по карте.';
  if (code) return 'Точность найденных координат ограничена — при необходимости подвиньте точку на карте.';
  return null;
};

const EditBuildingDialog = ({ open, onClose, building, onSaved, settlement = false }) => {
  const [form, setForm] = useState({
    name: '',
    address: '',
    latitude: null,
    longitude: null,
    heat_period_start_day: '',
    heat_period_end_day: '',
  });
  const [geoHint, setGeoHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && building) {
      setForm({
        name: building?.name || '',
        address: building?.address || '',
        latitude: normalizeCoordinate(building?.latitude),
        longitude: normalizeCoordinate(building?.longitude),
        heat_period_start_day:
          building?.heat_period_start_day != null ? String(building.heat_period_start_day) : '',
        heat_period_end_day:
          building?.heat_period_end_day != null ? String(building.heat_period_end_day) : '',
      });
      setGeoHint('');
      setError('');
    }
  }, [open, building]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const updateCoordinate = (field, raw) => {
    setForm((prev) => ({ ...prev, [field]: normalizeCoordinate(raw) }));
    setGeoHint('');
  };

  const applyGeoFromSuggestion = (suggestion) => {
    const lat = normalizeCoordinate(suggestion?.data?.geo_lat);
    const lon = normalizeCoordinate(suggestion?.data?.geo_lon);
    setForm((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lon,
    }));
    setGeoHint(qcGeoHint(suggestion?.data?.qc_geo) || '');
  };

  const mapDraftGateways = useMemo(() => {
    const lat = normalizeCoordinate(form.latitude);
    const lon = normalizeCoordinate(form.longitude);
    if (lat === null || lon === null) return [];
    return [
      {
        id: building?.id || 'draft-building',
        name: form.name || building?.name || 'Дом',
        latitude: lat,
        longitude: lon,
        address: form.address,
        status: 'UP',
        gateway_type: 'BS',
      },
    ];
  }, [building?.id, building?.name, form.address, form.latitude, form.longitude, form.name]);

  const handleSubmit = async () => {
    const name = (form.name || '').trim();
    const address = (form.address || '').trim();
    if (!name || !address) {
      setError('Укажите название и адрес.');
      return;
    }
    const periodStartDay =
      form.heat_period_start_day === '' ? null : Number(form.heat_period_start_day);
    const periodEndDay =
      form.heat_period_end_day === '' ? null : Number(form.heat_period_end_day);
    if (
      (periodStartDay !== null &&
        (Number.isNaN(periodStartDay) || periodStartDay < 1 || periodStartDay > 31)) ||
      (periodEndDay !== null &&
        (Number.isNaN(periodEndDay) || periodEndDay < 1 || periodEndDay > 31))
    ) {
      setError('Дни отчётного периода должны быть от 1 до 31.');
      return;
    }
    if ((periodStartDay === null) !== (periodEndDay === null)) {
      setError('Укажите оба дня периода или очистите оба.');
      return;
    }
    if (!building?.id) return;
    setSaving(true);
    setError('');
    try {
      await buildingsApi.updateBuilding(String(building.id), {
        name,
        address,
        latitude: normalizeCoordinate(form.latitude),
        longitude: normalizeCoordinate(form.longitude),
        heat_period_start_day: periodStartDay,
        heat_period_end_day: periodEndDay,
      });
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось сохранить изменения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{settlement ? 'Редактирование дома' : 'Редактирование здания'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Название"
            value={form.name}
            onChange={handleChange('name')}
            autoFocus
            fullWidth
          />
          <AddressAutocompleteField
            label="Адрес"
            value={form.address}
            onValueChange={(value) =>
              setForm((prev) => ({
                ...prev,
                address: value,
                latitude: null,
                longitude: null,
              }))
            }
            onSuggestionSelect={(suggestion) => {
              if (suggestion?.value) {
                setForm((prev) => ({ ...prev, address: suggestion.value }));
              }
              applyGeoFromSuggestion(suggestion);
            }}
          />
          {geoHint && <Alert severity="info">{geoHint}</Alert>}
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Период дома
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Заполняйте только если для дома нужен отдельный период. Пустые поля оставляют
              период по умолчанию. Срок добора задаётся в настройках комплекса.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Первый день периода"
                type="number"
                value={form.heat_period_start_day}
                onChange={handleChange('heat_period_start_day')}
                fullWidth
                inputProps={{ min: 1, max: 31 }}
                helperText="Оставьте пустым для настроек комплекса"
              />
              <TextField
                label="Последний день периода"
                type="number"
                value={form.heat_period_end_day}
                onChange={handleChange('heat_period_end_day')}
                fullWidth
                inputProps={{ min: 1, max: 31 }}
                helperText="Оставьте пустым для настроек комплекса"
              />
            </Stack>
          </Stack>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Точка на карте
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Проверьте положение точки и при необходимости сдвиньте её кликом по карте.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Широта"
                value={form.latitude ?? ''}
                onChange={(event) => updateCoordinate('latitude', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'decimal', step: 'any' }}
              />
              <TextField
                label="Долгота"
                value={form.longitude ?? ''}
                onChange={(event) => updateCoordinate('longitude', event.target.value)}
                fullWidth
                inputProps={{ inputMode: 'decimal', step: 'any' }}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setForm((prev) => ({ ...prev, latitude: null, longitude: null }));
                  setGeoHint('');
                }}
              >
                Очистить точку
              </Button>
            </Stack>
            <GatewayMapView
              gateways={mapDraftGateways}
              selectedGatewayId={building?.id || 'draft-building'}
              interactive
              height={260}
              onPickCoordinates={({ latitude, longitude }) => {
                setForm((prev) => ({
                  ...prev,
                  latitude: normalizeCoordinate(latitude),
                  longitude: normalizeCoordinate(longitude),
                }));
                setGeoHint('');
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !building?.id}
        >
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBuildingDialog;
